import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import { Tags } from "../Shared/Tags.js";
import { getProfileTypeConfig, getProfileTypeLabel, getTableColumns, getConfigurableColumns } from "./profileTypeCatalog.js";
import RowActionMenu from "./RowActionMenu.js";
import ConfirmRemovePopup from "./ConfirmRemovePopup.js";
import { CancelButton, SaveButton, ActionButtonsRow } from "./ActionButtons.js";

const formatStatCell = (stats, fieldKey, compareEnabled, snapshotDate, compareDate) => {
    if (!stats || stats[fieldKey] == null)
        return "—";

    const currentValue = stats[fieldKey];
    let title = `On ${snapshotDate} there were ${currentValue} items`;
    let differenceNode = null;

    if (compareEnabled) {
        const difference = stats[`${fieldKey}_Difference`];
        if (difference != null) {
            const abs = Math.abs(difference);
            const color = difference === 0 ? "#ccc" : (difference > 0 ? "#3b9c98" : "#9c3b3b");
            const sign = difference > 0 ? "+" : (difference < 0 ? "-" : "+");
            differenceNode = <span style={{ marginLeft: "3px", fontWeight: difference === 0 ? 400 : 700, color }}>({sign}{abs})</span>;
            title += `, ${difference >= 0 ? "+" : "-"}${abs} than on ${compareDate}`;
        }
    }

    return <span style={{ cursor: "help" }} title={title}>{currentValue}{differenceNode}</span>;
};

const ItemTagsCell = ({ item, onTagsChange }) => {
    if (!item?.UniqueId || !item?.TagObjectType)
        return <span>—</span>;

    const tagsContext = {
        currentItem: {
            Id: item.UniqueId,
            Tags: item.Tags || [],
        },
        setCurrentItem: (updated) => {
            onTagsChange(item.Id, updated?.Tags || []);
        },
        userRole: window.userRole || "Admin",
    };

    return (
        <div className="watchlists-item-tags" onClick={(e) => e.stopPropagation()}>
            <Tags context={tagsContext} objectType={item.TagObjectType} />
        </div>
    );
};

const ProfileTypeTab = ({
    workspaceId,
    profileType,
    snapshotDate,
    compareDate,
    compareEnabled,
    statsVersion = 0,
    onStatsUpdateComplete,
    onProfileTypeEmptied,
    columnsDialogOpen = false,
    onColumnsDialogClose,
}) => {

    const config = getProfileTypeConfig(profileType);
    const statsFields = config?.statsFields || [];
    const tableColumns = getTableColumns(config);
    const configurableColumns = getConfigurableColumns(config);
    const [items, setItems] = useState([]);
    const [statsById, setStatsById] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [error, setError] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pendingRemove, setPendingRemove] = useState(null);
    const [isRemovingItem, setIsRemovingItem] = useState(false);
    const [removeError, setRemoveError] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [draftEnabled, setDraftEnabled] = useState(() => new Set());
    const [isSavingColumns, setIsSavingColumns] = useState(false);
    const [columnsError, setColumnsError] = useState(null);
    const itemsReadyRef = useRef(false);

    const loadStats = ({ notifyComplete = false } = {}) => {
        const notify = () => {
            if (notifyComplete)
                onStatsUpdateComplete?.(profileType);
        };

        if (!config?.statsEndpoint) {
            setStatsById({});
            notify();
            return;
        }

        if (!snapshotDate) {
            notify();
            return;
        }

        const compare = compareEnabled ? compareDate : null;

        setIsLoadingStats(true);
        let url = `/umbraco/api/ListMonitoring/${config.statsEndpoint}?workspaceId=${workspaceId}&snapshotDate=${encodeURIComponent(snapshotDate)}`;
        if (compare)
            url += `&compareDate=${encodeURIComponent(compare)}`;

        fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error("Could not load stats");
                return response.json();
            })
            .then((data) => {
                const map = {};
                (data || []).forEach((row) => {
                    if (row && row.Id != null)
                        map[String(row.Id)] = row;
                });
                setStatsById(map);
            })
            .catch(() => setStatsById({}))
            .finally(() => {
                setIsLoadingStats(false);
                notify();
            });
    };

    const loadItems = () => {
        setIsLoading(true);
        setError(null);
        setStatsById({});
        itemsReadyRef.current = false;
        fetch(`/umbraco/api/WatchlistsApi/WatchlistItems?workspaceId=${workspaceId}&profileType=${encodeURIComponent(profileType)}`)
            .then((response) => {
                if (!response.ok) throw new Error("Could not load items");
                return response.json();
            })
            .then((data) => {
                setItems(data || []);
                setIsLoading(false);
                itemsReadyRef.current = true;
                if (config?.statsEndpoint)
                    loadStats();
            })
            .catch((err) => {
                setError(err.message);
                setIsLoading(false);
                itemsReadyRef.current = false;
            });
    };

    const loadColumnPreferences = () => {
        fetch(`/umbraco/api/WatchlistsApi/ProfileTypeColumns?profileType=${encodeURIComponent(profileType)}`)
            .then((response) => {
                if (!response.ok) throw new Error("Could not load column preferences");
                return response.json();
            })
            .then((data) => setHiddenColumns(data?.hidden || data?.Hidden || []))
            .catch(() => setHiddenColumns([]));
    };

    useEffect(() => {
        setItems([]);
        setStatsById({});
        setOpenMenuId(null);
        setMenuAnchor(null);
        setError(null);
        setHiddenColumns([]);
        loadItems();
        loadColumnPreferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId, profileType]);

    useEffect(() => {
        if (!columnsDialogOpen)
            return;
        const hidden = new Set(hiddenColumns);
        setDraftEnabled(new Set(configurableColumns.filter((col) => !hidden.has(col.key)).map((col) => col.key)));
        setColumnsError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsDialogOpen, profileType, hiddenColumns]);

    useEffect(() => {
        if (statsVersion <= 0)
            return;

        if (!itemsReadyRef.current || !config?.statsEndpoint) {
            onStatsUpdateComplete?.(profileType);
            return;
        }

        loadStats({ notifyComplete: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statsVersion]);

    const closeActionMenu = () => {
        setOpenMenuId(null);
        setMenuAnchor(null);
    };

    const toggleActionMenu = (itemId, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (openMenuId === itemId) {
            closeActionMenu();
            return;
        }
        setMenuAnchor(e.currentTarget);
        setOpenMenuId(itemId);
    };

    const askRemoveItem = (item) => {
        closeActionMenu();
        setRemoveError(null);
        setPendingRemove({
            id: item.Id,
            name: item.Name || item.Reference || "this item",
            isLast: items.length <= 1,
        });
    };

    const removeItem = () => {
        if (!pendingRemove?.id || isRemovingItem)
            return;

        const { id, isLast } = pendingRemove;
        setIsRemovingItem(true);
        setRemoveError(null);

        fetch(`/umbraco/api/WatchlistsApi/WatchlistItems?id=${id}`, { method: "DELETE" })
            .then(async (response) => {
                if (!response.ok) throw new Error("Could not remove item");

                if (!isLast) {
                    setPendingRemove(null);
                    loadItems();
                    return;
                }

                const typeResponse = await fetch(
                    `/umbraco/api/WatchlistsApi/WatchlistProfileTypes?workspaceId=${workspaceId}&profileType=${encodeURIComponent(profileType)}`,
                    { method: "DELETE" }
                );
                if (!typeResponse.ok)
                    throw new Error("Could not remove empty profile type");

                setPendingRemove(null);
                onProfileTypeEmptied?.(profileType);
            })
            .catch((err) => setRemoveError(err.message))
            .finally(() => setIsRemovingItem(false));
    };

    const hiddenColumnSet = new Set(hiddenColumns);
    const isColumnVisible = (key) => !hiddenColumnSet.has(key);
    const visibleStatsFields = statsFields.filter((field) => isColumnVisible(`stats:${field.key}`));
    const visibleTableColumns = tableColumns.filter((col) => {
        if (col.kind === "stats")
            return visibleStatsFields.length > 0;
        return true;
    });
    const hasStats = visibleStatsFields.length > 0;

    const toggleDraftColumn = (key) => {
        setDraftEnabled((prev) => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    };

    const saveColumnPreferences = () => {
        const hidden = configurableColumns
            .filter((col) => !draftEnabled.has(col.key))
            .map((col) => col.key);

        setIsSavingColumns(true);
        setColumnsError(null);
        const formData = new FormData();
        formData.append("profileType", profileType);
        formData.append("hiddenColumns", hidden.join(","));

        fetch("/umbraco/api/WatchlistsApi/ProfileTypeColumns", { method: "PUT", body: formData })
            .then(async (response) => {
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || "Could not save column preferences");
                }
                return response.json();
            })
            .then((data) => {
                setHiddenColumns(data?.hidden || data?.Hidden || hidden);
                onColumnsDialogClose?.();
            })
            .catch((err) => setColumnsError(err.message))
            .finally(() => setIsSavingColumns(false));
    };

    const renderHeaderRows = () => {
        if (!hasStats) {
            return (
                <tr>
                    {visibleTableColumns.map((col) => (
                        col.kind === "action"
                            ? <th key="h-action" style={{ width: "60px" }}>Action</th>
                            : <th key={`h-${col.key}`}>{col.label}</th>
                    ))}
                </tr>
            );
        }

        return (
            <>
                <tr>
                    {visibleTableColumns.map((col) => {
                        if (col.kind === "stats")
                            return <th key="h-stats-group" colSpan={visibleStatsFields.length} className="watchlists-stats-group-header">Statistics</th>;
                        if (col.kind === "action")
                            return <th key="h-action" rowSpan={2} style={{ width: "60px", verticalAlign: "middle" }}>Action</th>;
                        return <th key={`h-${col.key}`} rowSpan={2} style={{ verticalAlign: "middle" }}>{col.label}</th>;
                    })}
                </tr>
                <tr className="watchlists-stats-subheader">
                    {visibleStatsFields.map((sf) => (
                        <th key={`h-stat-${sf.key}`}>{sf.label}</th>
                    ))}
                </tr>
            </>
        );
    };

    const renderRowCells = (item, stats) => {
        const cells = [];
        visibleTableColumns.forEach((col) => {
            if (col.kind === "name") {
                cells.push(
                    <td key="c-name">
                        {item.ProfileUrl ? (
                            <a href={item.ProfileUrl} target="_blank" rel="noreferrer">{item.Name || item.Reference}</a>
                        ) : (
                            item.Name || item.Reference
                        )}
                    </td>
                );
            } else if (col.kind === "tags") {
                cells.push(
                    <td key="c-tags" style={{ maxWidth: "260px", minWidth: "140px" }}>
                        <ItemTagsCell
                            item={item}
                            onTagsChange={(itemId, tags) => {
                                setItems((prev) => prev.map((row) =>
                                    row.Id === itemId ? { ...row, Tags: tags } : row
                                ));
                            }}
                        />
                    </td>
                );
            } else if (col.kind === "extra") {
                if (col.key === "RiskScore") {
                    const score = item.Extra?.RiskScore;
                    const label = item.Extra?.RiskScoreLabel;
                    const riskClass = item.Extra?.RiskScoreClass || "";
                    cells.push(
                        <td key="c-RiskScore">
                            {score != null && score !== "" ? (
                                <span className={`profiles-risk-badge ${riskClass}`}>
                                    <strong>{score}</strong> <span className="mx-1">&mdash;</span> {label}
                                </span>
                            ) : "—"}
                        </td>
                    );
                } else {
                    cells.push(<td key={`c-${col.key}`}>{(item.Extra && item.Extra[col.key]) || "—"}</td>);
                }
            } else if (col.kind === "stats") {
                visibleStatsFields.forEach((sf) => {
                    cells.push(
                        <td key={`c-${sf.key}`}>
                            {isLoadingStats ? "…" : formatStatCell(stats, sf.key, compareEnabled, snapshotDate, compareDate)}
                        </td>
                    );
                });
            } else if (col.kind === "action") {
                cells.push(
                    <td key="c-action">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={(e) => toggleActionMenu(item.Id, e)} style={{ minWidth: "32px" }}>
                            <i className="fa fa-ellipsis-v" />
                        </button>
                    </td>
                );
            }
        });
        return cells;
    };

    return (
        <div key={`${workspaceId}-${profileType}`}>
            {error && <div style={{ color: "#e53e3e", marginBottom: "10px" }}>{error}</div>}

            {isLoading ? <LoadingSpinnerImage /> : (
                items.length === 0 ? (
                    <div className="watchlists-empty">No items added yet for this profile type.</div>
                ) : visibleTableColumns.length === 0 ? (
                    <div className="watchlists-empty">No columns selected. Use the menu to choose which columns to show.</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="watchlists-items-table">
                            <thead>
                                {renderHeaderRows()}
                            </thead>
                            <tbody>
                                {items.map((item) => {
                                    const statsKey = item.StatsId != null ? String(item.StatsId) : "";
                                    const stats = statsKey ? (statsById[statsKey] || {}) : {};
                                    return (
                                        <tr key={item.Id}>
                                            {renderRowCells(item, stats)}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            <RowActionMenu open={openMenuId != null} anchorEl={menuAnchor} onClose={closeActionMenu}>
                {(() => {
                    const openItem = items.find((i) => i.Id === openMenuId);
                    if (!openItem) return null;
                    return (
                        <>
                            {openItem.ProfileUrl && (
                                <a href={openItem.ProfileUrl} target="_blank" rel="noreferrer" className="watchlists-row-action-menu-item" onClick={closeActionMenu}>
                                    Show More Information
                                </a>
                            )}
                            {openItem.ProfileUrl && <div className="watchlists-row-action-menu-divider" />}
                            <button type="button" className="watchlists-row-action-menu-item is-danger" onClick={() => askRemoveItem(openItem)}>
                                Remove
                            </button>
                        </>
                    );
                })()}
            </RowActionMenu>

            {pendingRemove && (
                <ConfirmRemovePopup
                    title="Remove item"
                    confirmLabel="Remove"
                    isRemoving={isRemovingItem}
                    error={removeError}
                    onCancel={() => { if (!isRemovingItem) setPendingRemove(null); }}
                    onConfirm={removeItem}
                    message={(
                        <>
                            Are you sure you want to remove this item from the watchlist:
                            <br />
                            <br />
                            "{pendingRemove.name}"
                            {pendingRemove.isLast && (
                                <>
                                    <br />
                                    <br />
                                    This is the last {getProfileTypeLabel(profileType)} item. Removing it will also remove this section from the watchlist.
                                </>
                            )}
                        </>
                    )}
                />
            )}

            {columnsDialogOpen && (
                <div
                    className="watchlists-note-popup-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget && !isSavingColumns)
                            onColumnsDialogClose?.();
                    }}
                >
                    <div className="add-task-popup watchlists-columns-popup">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
                            <h3 style={{ margin: 0 }}>Configure columns</h3>
                            <button type="button" className="close-button" onClick={onColumnsDialogClose} aria-label="Close" disabled={isSavingColumns}>
                                <i className="fas fa-times" />
                            </button>
                        </div>
                        <p style={{ margin: "0 0 12px", color: "#4a5568", fontSize: "13px" }}>
                            Choose which statistics columns appear. This is saved for your user.
                        </p>
                        {columnsError && <div style={{ color: "#e53e3e", marginBottom: "10px" }}>{columnsError}</div>}
                        <div className="watchlists-columns-list">
                            {configurableColumns.map((col) => (
                                <label key={col.key} className="watchlists-columns-option">
                                    <input
                                        type="checkbox"
                                        checked={draftEnabled.has(col.key)}
                                        onChange={() => toggleDraftColumn(col.key)}
                                        disabled={isSavingColumns}
                                    />
                                    <span>{col.label}</span>
                                </label>
                            ))}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                            <ActionButtonsRow isLoading={isSavingColumns}>
                                <CancelButton onClick={onColumnsDialogClose} disabled={isSavingColumns} />
                                <SaveButton onClick={saveColumnPreferences} disabled={isSavingColumns} />
                            </ActionButtonsRow>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileTypeTab;
